/* global debounce */

if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.backBtn = this.querySelector('.js-search-back');
      this.addListeners();
      this.init();
    }

    addListeners() {
      // Belt-and-braces: listen to multiple event types because Searchspring
      // can hijack/swallow the native 'input' event on the search field.
      const syncBound = this.syncResetButton.bind(this);
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      this.input.addEventListener('input', syncBound);
      this.input.addEventListener('keyup', syncBound);
      this.input.addEventListener('change', syncBound);
      this.input.addEventListener('paste', () => setTimeout(syncBound, 10));
      this.input.addEventListener('cut', () => setTimeout(syncBound, 10));

      // MutationObserver as a final safety net: catches programmatic
      // value changes (e.g. Searchspring autofilling the input) that
      // would otherwise not fire any event.
      try {
        const obs = new MutationObserver(syncBound);
        obs.observe(this.input, { attributes: true, attributeFilter: ['value'] });
      } catch (e) { /* noop */ }

      // Re-sync periodically for the first few seconds after load,
      // in case Searchspring rehydrates the input and clobbers state.
      setTimeout(syncBound, 500);
      setTimeout(syncBound, 1500);

      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.resetBtn.addEventListener('click', this.handleReset.bind(this));

      // Mobile fullscreen search.
      // Use 'pointerdown' (fires before focus) so the fullscreen class
      // is applied BEFORE the keyboard/layout shift, eliminating the
      // jumpy "arrow moves to top" transition flash.
      this.input.addEventListener('pointerdown', this.handleSearchFocus.bind(this));
      this.input.addEventListener('focus', this.handleSearchFocus.bind(this));
      if (this.backBtn) {
        this.backBtn.addEventListener('click', this.exitFullscreen.bind(this));
      }
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && document.body.classList.contains('search--fullscreen')) {
          this.exitFullscreen();
        }
      });
    }

    init() {
      if ((this.input.dataset.placeholderTwo || this.input.dataset.placeholderThree)
        && (this.input.dataset.placeholderPromptsMob === 'true' || theme.mediaMatches.md)) {
        this.typeInPlaceholders();
      }
    }

    /**
     * Simulates the placeholder titles being typed in and the previous one deleted, on repeat
     */
    typeInPlaceholders() {
      const typingSpeed = this.input.dataset.typingSpeed || 100;
      const deletingSpeed = this.input.dataset.deletingSpeed || 60;
      const delayAfterDeleting = this.input.dataset.delayAfterDeleting || 500;
      const delayBeforeFirstDelete = this.input.dataset.delayBeforeFirstDelete || 2000;
      const delayAfterWordTyped = this.input.dataset.delayAfterWordTyped || 2400;
      const placeholders = [];

      if (this.input.placeholder) {
        placeholders.push(this.input.placeholder);
      }

      if (this.input.dataset.placeholderTwo) {
        placeholders.push(this.input.dataset.placeholderTwo);
      }

      if (this.input.dataset.placeholderThree) {
        placeholders.push(this.input.dataset.placeholderThree);
      }

      const typeInNextPlaceholder = async (placeholder) => {
        await new Promise((resolve) => {
          let currentPlaceholder = this.input.getAttribute('placeholder');

          // If necessary remove the start of the word to type in if it already exists in the
          // placeholder
          let nextPlaceholder = (currentPlaceholder.length >= 3
              && placeholder.startsWith(currentPlaceholder))
            ? placeholder.replace(currentPlaceholder, '')
            : placeholder;

          const typingIntervalId = setInterval(() => {
            currentPlaceholder = this.input.getAttribute('placeholder');
            this.input.setAttribute('placeholder', currentPlaceholder + nextPlaceholder.charAt(0));
            if (nextPlaceholder.length === 1) {
              resolve();
              clearInterval(typingIntervalId); // Stop typing
            } else {
              nextPlaceholder = nextPlaceholder.substring(1);
            }
          }, typingSpeed);
        });
      };

      const deleteCurrentPlaceholder = async (nextPlaceholder) => {
        await new Promise((resolve) => {
          let prevPlaceholder = this.input.getAttribute('placeholder');
          const deletionIntervalId = setInterval(() => {
            const newPlaceholder = prevPlaceholder.substring(0, prevPlaceholder.length - 1);
            this.input.setAttribute('placeholder', newPlaceholder);
            prevPlaceholder = newPlaceholder;
            if (prevPlaceholder.length === 0
              || (prevPlaceholder.length >= 3 && nextPlaceholder.startsWith(prevPlaceholder))) {
              resolve();
              clearInterval(deletionIntervalId);
            }
          }, deletingSpeed);
        });
      };

      let startIndex = 0;
      const showNextPlaceholder = () => {
        startIndex = (startIndex + 1) % placeholders.length;
        const nextPlaceholder = placeholders[startIndex];
        deleteCurrentPlaceholder.call(this, nextPlaceholder).then(() => {
          setTimeout(() => {
            typeInNextPlaceholder.call(this, nextPlaceholder).then(() => {
              setTimeout(showNextPlaceholder.bind(this), delayAfterWordTyped);
            });
          }, delayAfterDeleting);
        });
      };

      setTimeout(showNextPlaceholder.bind(this), delayBeforeFirstDelete);
    }

    /**
     * Syncs reset button visibility + dirty state from current input value.
     * Called from multiple events because Searchspring hijacks the native
     * 'input' event, so we need belt-and-braces coverage.
     */
    syncResetButton() {
      const hasValue = this.input.value && this.input.value.length > 0;
      if (hasValue) {
        this.input.classList.add('search__input--dirty');
        this.classList.add('search__form--has-value');
        if (this.resetBtn) {
          this.resetBtn.removeAttribute('hidden');
          this.resetBtn.style.display = 'flex';
        }
      } else {
        this.input.classList.remove('search__input--dirty');
        this.classList.remove('search__form--has-value');
        if (this.resetBtn) {
          this.resetBtn.setAttribute('hidden', '');
          this.resetBtn.style.display = '';
        }
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      this.syncResetButton();
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
    }

    /**
     * Handles the reset button being clicked
     */
    handleReset() {
      this.input.value = '';
      this.input.classList.remove('search__input--dirty');
      if (this.resetBtn) this.resetBtn.setAttribute('hidden', '');
      this.input.focus();
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    /**
     * On mobile focus, expands search to fullscreen overlay
     */
    handleSearchFocus() {
      if (window.innerWidth < 1024) {
        this.enterFullscreen();
      }
    }

    enterFullscreen() {
      document.body.classList.add('search--fullscreen');
      if (this.backBtn) this.backBtn.removeAttribute('hidden');
    }

    exitFullscreen() {
      document.body.classList.remove('search--fullscreen');
      if (this.backBtn) this.backBtn.setAttribute('hidden', '');
      this.input.blur();
    }
  }

  customElements.define('search-form', SearchForm);
}
